import { apiUrl } from "../utils/api";

export type ImportKind = "contacts" | "contracts";

export type ConflictKind =
  | "none"
  | "hard"
  | "soft"
  | "both"
  | "invalid";

export type DuplicateReasonCode =
  | "kundennummer_match"
  | "vertragsnummer_match"
  | "firma_email"
  | "firma_address"
  | "firma_phone"
  | "name_birthdate"
  | "name_only"
  | "lastname_email";

export interface DuplicateReason {
  code: DuplicateReasonCode;
  detail?: string;
}

export type RowDecisionAction =
  | "create"
  | "skip"
  | "update"
  | "merge"
  | "force_create";

export interface RowDecision {
  action: RowDecisionAction;
  targetId?: number;
  fieldSources?: Record<string, "excel" | "existing">;
}

export interface PreviewSummary {
  batchId: number;
  totalRows: number;
  noConflict: number;
  hardConflicts: number;
  softConflicts: number;
  invalid: number;
}

export interface PreviewRow {
  rowId: number;
  rowIndex: number;
  payload: any;
  conflictKind: ConflictKind;
  reasons: DuplicateReason[];
  matchIds: number[];
  matches?: any[];
  decision?: RowDecision | null;
}

export interface CommitResult {
  created: number;
  updated: number;
  merged: number;
  skipped: number;
  failed: number;
}

export const importService = {
  async preview(kind: ImportKind, file: File): Promise<PreviewSummary> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(apiUrl(`import/${kind}/preview`), {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error(`Preview failed: HTTP ${res.status}`);
    return res.json();
  },

  async listRows(
    batchId: number,
    filter: ConflictKind | "all" = "all",
    limit = 200,
    offset = 0,
  ): Promise<{ rows: PreviewRow[]; total: number }> {
    const res = await fetch(
      apiUrl(`import/${batchId}/rows`, { filter, limit, offset }),
    );
    if (!res.ok) throw new Error(`List rows failed: HTTP ${res.status}`);
    return res.json();
  },

  async setDecision(
    batchId: number,
    rowId: number,
    decision: RowDecision,
  ): Promise<void> {
    const res = await fetch(
      apiUrl(`import/${batchId}/rows/${rowId}/decision`),
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(decision),
      },
    );
    if (!res.ok) throw new Error(`Set decision failed: HTTP ${res.status}`);
  },

  async bulkDecision(
    batchId: number,
    filter: ConflictKind | "all",
    decision: RowDecision,
  ): Promise<{ updated: number }> {
    const res = await fetch(apiUrl(`import/${batchId}/bulk-decision`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filter, decision }),
    });
    if (!res.ok) throw new Error(`Bulk decision failed: HTTP ${res.status}`);
    return res.json();
  },

  async commit(batchId: number): Promise<CommitResult> {
    const res = await fetch(apiUrl(`import/${batchId}/commit`), {
      method: "POST",
    });
    if (!res.ok) throw new Error(`Commit failed: HTTP ${res.status}`);
    return res.json();
  },
};

export const formatReason = (r: DuplicateReason): string => {
  switch (r.code) {
    case "kundennummer_match":
      return r.detail || "Gleiche Kundennummer";
    case "vertragsnummer_match":
      return r.detail || "Gleiche Vertragsnummer";
    case "firma_email":
      return "Firmenname + E-Mail";
    case "firma_address":
      return "Firmenname + Anschrift";
    case "firma_phone":
      return "Firmenname + Telefon";
    case "name_birthdate":
      return "Vorname + Nachname + Geburtsdatum";
    case "name_only":
      return "Vorname + Nachname";
    case "lastname_email":
      return "Nachname + E-Mail";
    default:
      return r.code;
  }
};
